import { DomParser } from '../../Constants';
import { AppComponents } from '@configuration/AppComponents';
import { ILocalizedLabel, LocalizeLabel } from '@localization/helpers';
import { IPromiseCache, cachedWrapper } from '@utilities/MemoryCachingHelpers';
import { sendMetadataGetRequestAsync } from './MetadataApi';
import { sanitizeGuid } from '../../Functions';
import { IODataResponse } from '@src/app/interfaces/general';
import { LocalizedLabel } from '@src/app/interfaces/entitydefinition';

export enum FormType {
    Main = 2,
    Dialog = 8,
    QuickCreate = 7
}
export enum ErrorType {
    MissingEntityName,
    MissingQuickCreateForm,
    MissingDefaultForm
}
interface FormDisplayOrder {
    formId: string;
    order: number;
}
interface BasicForm {
    formId: string;
    name: LocalizedLabel[];
    description: LocalizedLabel[];
}
interface Form extends BasicForm {
    type: FormType;
    formXml: string;
}

export class FormDefinition {
    private static _formDefinitionCache: IPromiseCache<FormDefinition> = {};
    private static _defaultQuickCreateDefinitionCache: IPromiseCache<FormDefinition> = {};
    private static _defaultEntityFormCache: IPromiseCache<string> = {};
    private static _entityFormIdNameCache: {
        [entityName: string]: Promise<{
            formid: string,
            name: string
        }[]>
    } = {};

    public Id: string;
    public Type: FormType;
    public FormXml: Document;
    public Labels: { [key: string]: ILocalizedLabel[] };

    /**
     * This method performs a lookup for form ID if it is not know and caches results.
     * Use one of parameters to identify the form you want to retrieve
     * @param entityName If you don't know a specific form you can use a defaut form for an entity
     * @param formId Use this parameter when you know ID of the form you want
     * @param formUniqueName Form names are usually used when opening dialogs from the cluent SDK
     * @returns 
     */
    static async getAsync(entityName?: string, formId?: string, formUniqueName?: string): Promise<FormDefinition> {
        if (formId) {
            formId = sanitizeGuid(formId);
        }
        if (!entityName && !formId && !formUniqueName) {
            throw new Error("You need to specify an entity name, form ID or form unique name!");
        }
        else if (entityName && !formId && !formUniqueName) {
            formId = await this.getEntityDefaultFormId(entityName);
        }

        return cachedWrapper(formId ?? formUniqueName, async () => {
            return this._getFormDefinitionAsync(formId, formUniqueName);
        }, this._formDefinitionCache);
    }

    static async getDefaultQuickCreateAsync(entityName?: string): Promise<FormDefinition> {
        if (!entityName) {
            throw { code: ErrorType.MissingEntityName, message: "You need to specify an entity name!" };
        }

        return cachedWrapper(entityName, async () => {
            const formIds = await AppComponents.filterAppComponents(entityName, 60);

            const formOrderInfo = await this._getFormsOrderInfo(entityName, FormType.QuickCreate);
            const form = formOrderInfo.find(x => formIds.includes(x.formId));

            if (!form) {
                throw { code: ErrorType.MissingQuickCreateForm, message: `Default quick create form for entity ${entityName} was not found in metadata!` };
            }

            return this._getFormDefinitionAsync(form.formId);
        }, this._defaultQuickCreateDefinitionCache);
    }

    private static async _getFormsOrderInfo(entityName: string, formType: FormType): Promise<FormDisplayOrder[]> {
        const response = await sendMetadataGetRequestAsync(`v1.0/talxis_GetFormsOrderInfo(EntityName='${entityName}',FormType=${formType})`);
        if (!response.ok) {
            throw new Error(`Failed to retrieve form order information: ${response.statusText}`);
        }
        const formOrderInfo: IODataResponse<FormDisplayOrder> = await response.json();
        return formOrderInfo.value;
    }

    private static async _getFormDefinitionAsync(formId?: string, uniqueName?: string): Promise<FormDefinition> {
        const parameters: string[] = [];
        if (formId) {
            parameters.push(`FormId='${formId}'`);
        }
        if (uniqueName) {
            parameters.push(`UniqueName='${uniqueName}'`);
        }

        const response = await sendMetadataGetRequestAsync(`v1.0/talxis_GetForm(${parameters.join(',')})`);
        if (!response.ok) {
            throw new Error(`Failed to retrieve form definition: ${response.statusText}: ${await response.text()}`);
        }
        const formData: Form = await response.json();

        return {
            Id: formData.formId,
            FormXml: DomParser.parseFromString(formData.formXml, "text/xml"),
            Type: formData.type,
            Labels: {
                "name": formData.name,
                "description": formData.description
            }
        };
    }

    public static async getEntityDefaultFormId(entityName: string): Promise<string> {
        return cachedWrapper(entityName, async () => {
            const formIds = await AppComponents.filterAppComponents(entityName, 60);

            const forms = await this._getFormsOrderInfo(entityName, FormType.Main);
            if (forms.length === 0) {
                throw { code: ErrorType.MissingDefaultForm, message: `No forms found for entity ${entityName}` };
            }

            let form;
            if (formIds.length === 0) {
                form = forms[0];
            } else {
                form = forms.find(x => formIds.includes(x.formId));
            }
            if (!form) {
                throw { code: ErrorType.MissingDefaultForm, message: `Unable to find default form for entity ${entityName}` };
            }

            return form.formId;
        }, this._defaultEntityFormCache);
    }

    public static async getFormNamesAndIds(entityName: string, formType?: FormType): Promise<{ formid: string; name: string }[]> {
        let cacheKey = entityName;
        if (formType) {
            cacheKey += `_${formType}`;
        }
        return cachedWrapper(cacheKey, async () => {
            const formIds = await AppComponents.filterAppComponents(entityName, 60);

            const parameters = [`EntityName='${entityName}'`];
            if (formType) {
                parameters.push(`FormType=${formType}`);
            }
            if (formIds.length > 0) {
                parameters.push(`FormIds=[${formIds.map(x => `'${x}'`).join(',')}]`);
            }
            const result = await sendMetadataGetRequestAsync(`v1.0/talxis_GetForms(${parameters.join(',')})`);
            if (!result.ok) {
                throw new Error(`Failed to retrieve form names and IDs: ${result.statusText}`);
            }

            const formNamesAndIds: IODataResponse<BasicForm> = await result.json();
            return formNamesAndIds.value.map(x => {
                return {
                    formid: x.formId,
                    name: LocalizeLabel(x.name)
                };
            });
        }, this._entityFormIdNameCache);
    }
}